import { jsx as _jsx } from "react/jsx-runtime";
import * as React from "react";
import { cva } from "class-variance-authority";
import { useRender } from "@base-ui-components/react/use-render";
import { cn } from "./lib/utils.js";
// The icon column is sized with `--spacing(4)`, not `var(--spacing)`: under the
// `ui` prefix the theme var is `--ui-spacing`, so a raw `var(--spacing)` is
// undefined outside a Tailwind host, the column template drops to `none`, and
// the implicit auto icon column stretches to push a short title inward.
//
// An AlertAction adds a trailing auto column. With an icon, the icon's gap-x-3
// already separates the text from the action. Without one, a column gap would
// also open between the empty first column and the title and indent it, so the
// separation is a fixed spacer track instead. The action targets the last
// column, so it lands correctly in either template.
const alertVariants = cva("ui:relative ui:grid ui:w-full ui:grid-cols-[0_1fr] ui:items-start ui:gap-y-0.5 ui:rounded-lg ui:border ui:border-border ui:px-4 ui:py-3 ui:text-sm ui:has-[>svg]:grid-cols-[--spacing(4)_1fr] ui:has-[>svg]:gap-x-3 ui:has-[[data-slot=alert-action]]:not-has-[>svg]:grid-cols-[0_1fr_--spacing(3)_auto] ui:has-[>svg]:has-[[data-slot=alert-action]]:grid-cols-[--spacing(4)_1fr_auto] ui:[&>svg]:size-4 ui:[&>svg]:translate-y-0.5 ui:[&>svg]:text-current", {
    variants: {
        variant: {
            default: "ui:bg-surface-1 ui:text-fg",
            destructive: "ui:text-error ui:*:data-[slot=alert-description]:text-error/80 ui:[&>svg]:text-current",
        },
    },
    defaultVariants: {
        variant: "default",
    },
});
function Alert({ className, variant, children, ...props }) {
    return (_jsx("div", { "data-slot": "alert", role: "alert", className: cn(alertVariants({ variant }), className), ...props, children: children }));
}
function AlertTitle({ className, ...props }) {
    return (_jsx("div", { "data-slot": "alert-title", className: cn("ui:col-start-2 ui:line-clamp-1 ui:min-h-4 ui:font-medium ui:tracking-tight", className), ...props }));
}
function AlertDescription({ className, ...props }) {
    return (_jsx("div", { "data-slot": "alert-description", className: cn("ui:col-start-2 ui:grid ui:justify-items-start ui:gap-1 ui:text-sm ui:text-fg-muted ui:[&_p]:leading-6", className), ...props }));
}
const AlertAction = React.forwardRef(({ className, asChild = false, children, ...props }, ref) => {
    // With `asChild`, our children prop is the element we compose onto, so it
    // is never forwarded — the child keeps its own children.
    const child = asChild && React.isValidElement(children)
        ? children
        : null;
    return useRender({
        render: child ?? _jsx("div", {}),
        ref,
        props: {
            "data-slot": "alert-action",
            className: cn("ui:col-start-[-2] ui:row-span-2 ui:row-start-1 ui:flex ui:items-start ui:self-center", className),
            ...props,
            ...(child ? null : { children }),
        },
    });
});
AlertAction.displayName = "AlertAction";
export { Alert, AlertTitle, AlertDescription, AlertAction };
